import pluralize from "pluralize";

type ProgressClause = {
	verb: string;
	clause: string;
};

type TopicSummary = {
	text: string;
	coversAll: boolean;
};

type SessionSummaryInput = {
	progresses: string[];
	itemCount: number;
	modeLabel: string;
	selectedTopics: string[];
	totalTopicCount: number;
	timeEstimate?: string;
};

const VERB_BY_PROGRESS: Record<string, string> = {
	none: "Study",
	low: "Study",
	medium: "Practice",
	high: "Refresh",
};

const DESC_BY_PROGRESS: Record<string, string> = {
	none: "unanswered",
	low: "need work",
	medium: "almost there",
	high: "mastered",
};

const normalizeProgressKey = (progresses: string[]): string => {
	const uniq = Array.from(new Set(progresses)).sort();
	if (uniq.length === 0) {
		return "noneSelected";
	}
	if (uniq.length === 4) {
		return "all";
	}
	return uniq.join("+");
};

const getBeVerb = (count: number): string => (count === 1 ? "is" : "are");

const toClausePhrase = (desc: string, itemCount: number): string => {
	if (desc === "need work") {
		const needVerb = itemCount === 1 ? "needs" : "need";
		return `${needVerb} work`;
	}
	return `${getBeVerb(itemCount)} ${desc}`;
};

const joinClausePhrases = (phrases: string[]): string => {
	if (phrases.length === 1) {
		return phrases[0];
	}
	if (phrases.length === 2) {
		return `${phrases[0]} or ${phrases[1]}`;
	}
	return `${phrases.slice(0, -1).join(", ")}, or ${phrases[phrases.length - 1]}`;
};

const buildProgressClause = (
	progressKey: string,
	itemCount: number,
): ProgressClause => {
	if (progressKey === "all") {
		return { verb: "Study", clause: " from all mastery levels" };
	}
	if (progressKey === "noneSelected") {
		return { verb: "Study", clause: "" };
	}

	if (VERB_BY_PROGRESS[progressKey]) {
		const desc = DESC_BY_PROGRESS[progressKey];
		const phrase = toClausePhrase(desc, itemCount);
		return {
			verb: VERB_BY_PROGRESS[progressKey],
			clause: ` that ${phrase}`,
		};
	}

	const parts = progressKey.split("+").map((k) => k.trim());
	const descriptions = parts.map((k) => DESC_BY_PROGRESS[k] || k);
	const phrases = descriptions.map((d) => toClausePhrase(d, itemCount));
	return {
		verb: "Work on",
		clause: ` that ${joinClausePhrases(phrases)}`,
	};
};

const summarizeTopics = (
	selected: string[],
	totalCount: number,
): TopicSummary => {
	const n = selected.length;
	if (n === 0 || n === totalCount) {
		return {
			text: `all ${totalCount} ${pluralize("topic", totalCount)}`,
			coversAll: true,
		};
	}
	if (n === 1) {
		return { text: selected[0], coversAll: false };
	}
	if (n === 2) {
		return { text: `${selected[0]} and ${selected[1]}`, coversAll: false };
	}
	return {
		text: `${selected[0]}, ${selected[1]}, and ${n - 2} more`,
		coversAll: false,
	};
};

const maybeAdjustVerbForTopics = (verb: string, coversAll: boolean): string => {
	if (coversAll && verb === "Work on") {
		return "Study";
	}
	return verb;
};

export const getSessionSummary = ({
	progresses,
	itemCount,
	modeLabel,
	selectedTopics,
	totalTopicCount,
	timeEstimate,
}: SessionSummaryInput): string => {
	const progressKey = normalizeProgressKey(progresses);
	let { verb, clause } = buildProgressClause(progressKey, itemCount);

	const { text: topicText, coversAll } = summarizeTopics(
		selectedTopics,
		totalTopicCount,
	);
	verb = maybeAdjustVerbForTopics(verb, coversAll);

	const timeText = timeEstimate ? ` (about ${timeEstimate})` : "";
	const topicPhrase = topicText
		? coversAll
			? ` across ${topicText}`
			: ` from ${topicText}`
		: "";

	return `${verb} ${itemCount} ${pluralize(modeLabel, itemCount)}${topicPhrase}${clause}${timeText}.`;
};
